import React, { useState, useEffect } from 'react';
import filtersData from '../data/filters.json';
import './FiltersPage.css';

interface FilterData {
  id: string;
  type: 'range' | 'multiselect';
  label: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: string[];
  defaultValue: number[] | string[];
}

interface FilterValues {
  [key: string]: number[] | string[];
}

const FiltersPage: React.FC = () => {
  const [filterValues, setFilterValues] = useState<FilterValues>({});

  // Initialize filter values with defaults
  useEffect(() => {
    const initialValues: FilterValues = {};
    filtersData.forEach((filter) => {
      const typedFilter = filter as FilterData;
      if (typedFilter.type === 'range') {
        initialValues[typedFilter.id] = [...(typedFilter.defaultValue as number[])];
      } else {
        initialValues[typedFilter.id] = [...(typedFilter.defaultValue as string[])];
      }
    });
    setFilterValues(initialValues);
  }, []);

  const handleRangeChange = (filterId: string, index: number, value: number) => {
    setFilterValues(prev => {
      const currentRange = prev[filterId] as number[];
      const newRange = [...currentRange];
      newRange[index] = value;
      
      // Ensure min doesn't exceed max and vice versa
      if (index === 0 && newRange[0] > newRange[1]) {
        newRange[1] = newRange[0];
      } else if (index === 1 && newRange[1] < newRange[0]) {
        newRange[0] = newRange[1];
      }
      
      return { ...prev, [filterId]: newRange };
    });
  };

  const handleMultiSelectChange = (filterId: string, option: string) => {
    setFilterValues(prev => {
      const currentSelection = prev[filterId] as string[];
      const newSelection = currentSelection.includes(option)
        ? currentSelection.filter(item => item !== option)
        : [...currentSelection, option];
      
      return { ...prev, [filterId]: newSelection };
    });
  };

  const clearAllFilters = () => {
    const clearedValues: FilterValues = {};
    filtersData.forEach((filter) => {
      const typedFilter = filter as FilterData;
      if (typedFilter.type === 'range') {
        clearedValues[typedFilter.id] = [...(typedFilter.defaultValue as number[])];
      } else {
        clearedValues[typedFilter.id] = [...(typedFilter.defaultValue as string[])];
      }
    });
    setFilterValues(clearedValues);
  };

  const renderRangeFilter = (filter: FilterData) => {
    const currentRange = filterValues[filter.id] as number[] || filter.defaultValue as number[];
    const [min, max] = currentRange;
    const range = filter.max! - filter.min!;
    const minPercent = ((min - filter.min!) / range) * 100;
    const maxPercent = ((max - filter.min!) / range) * 100;

    return (
      <div key={filter.id} className="filter-item">
        <div className="filter-header">
          <h3>{filter.label}</h3>
          <span className="filter-value">
            {min} - {max} {filter.unit}
          </span>
        </div>
        <div className="range-container">
          <div className="dual-range-wrapper">
            <div className="dual-range-track">
              <div 
                className="dual-range-fill"
                style={{
                  left: `${minPercent}%`,
                  width: `${maxPercent - minPercent}%`
                }}
              />
            </div>
            <input
              type="range"
              min={filter.min}
              max={filter.max}
              step={filter.step}
              value={min}
              onChange={(e) => handleRangeChange(filter.id, 0, parseInt(e.target.value))}
              className="dual-range-input dual-range-min"
            />
            <input
              type="range"
              min={filter.min}
              max={filter.max}
              step={filter.step}
              value={max}
              onChange={(e) => handleRangeChange(filter.id, 1, parseInt(e.target.value))}
              className="dual-range-input dual-range-max"
            />
          </div>
          <div className="range-labels">
            <span className="range-label-min">{min} {filter.unit}</span>
            <span className="range-label-max">{max} {filter.unit}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderMultiSelectFilter = (filter: FilterData) => {
    const currentSelection = filterValues[filter.id] as string[] || [];

    return (
      <div key={filter.id} className="filter-item">
        <div className="filter-header">
          <h3>{filter.label}</h3>
          <span className="filter-count">
            {currentSelection.length} selected
          </span>
        </div>
        <div className="multiselect-container">
          {filter.options?.map((option) => (
            <button
              key={option}
              className={`option-chip ${currentSelection.includes(option) ? 'selected' : ''}`}
              onClick={() => handleMultiSelectChange(filter.id, option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="filters-page">
      <div className="filters-header">
        <h1>Filters</h1>
        <button onClick={clearAllFilters} className="clear-button">
          Clear All
        </button>
      </div>
      
      <div className="filters-container">
        {filtersData.map((filter) => {
          const typedFilter = filter as FilterData;
          return typedFilter.type === 'range' 
            ? renderRangeFilter(typedFilter)
            : renderMultiSelectFilter(typedFilter);
        })}
      </div>


    </div>
  );
};

export default FiltersPage;
